import { useCallback, useEffect, useRef } from 'react';
import { useFileStore } from '@/lib/file-store';
import { useWebContainerStore } from '@/lib/webcontainer-store';

type FileSyncHookParams = {
  siteId: string;
  themeId: string;
  themeBuildId: string;
  enabled?: boolean;
  debounceMs?: number; // Debounce time in milliseconds (default 2000ms)
  initialLoadComplete?: boolean; // Flag to indicate initial files have been loaded
};

// Helper to extract all files from the file tree
function extractAllFiles(nodes: any[], files: { path: string; content: string }[] = []): { path: string; content: string }[] {
  nodes.forEach(node => {
    if (node.type === 'file' && node.path && node.content !== undefined) {
      files.push({
        path: node.path,
        content: node.content || ''
      });
    }
    if (node.children && node.children.length > 0) {
      extractAllFiles(node.children, files);
    }
  });
  return files;
}

export function useFileSync({ siteId, themeId, themeBuildId, enabled = true, debounceMs = 200, initialLoadComplete = false }: FileSyncHookParams) {
  const lastSyncRef = useRef<number>(0);
  const syncInProgressRef = useRef<boolean>(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingSyncRef = useRef<boolean>(false);

  const syncFiles = useCallback(async () => {
    if (!enabled || !siteId || !themeId || !themeBuildId || syncInProgressRef.current) {
      pendingSyncRef.current = true;
      return;
    }

    syncInProgressRef.current = true;
    pendingSyncRef.current = false;

    try {
      // Get current files from store
      const files = useFileStore.getState().files;

      // Extract all files from the file tree
      const allFiles = extractAllFiles(files);

      console.log(`Syncing ${allFiles.length} files to backend...`);

      const response = await fetch(
        `/sites/${siteId}/themes/${themeId}/theme_builds/${themeBuildId}/ai_code_assistants/sync_files`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          },
          body: JSON.stringify({ files: allFiles })
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log('✅ File sync successful:', result);
        lastSyncRef.current = Date.now();

        // Trigger preview refresh after successful sync
        const { triggerRefresh } = useWebContainerStore.getState();
        setTimeout(() => {
          console.log('🔄 Triggering preview refresh after sync...');
          triggerRefresh();
        }, 300);

        return result;
      } else {
        console.error('❌ File sync failed:', await response.text());
        return { success: false };
      }
    } catch (error) {
      console.error('❌ File sync error:', error);
      return { success: false, error };
    } finally {
      syncInProgressRef.current = false;

      // If there's a pending sync, trigger it after a delay
      if (pendingSyncRef.current) {
        setTimeout(() => syncFiles(), 1000);
      }
    }
  }, [siteId, themeId, themeBuildId, enabled]);

  // Reactive sync: Listen to file store changes
  useEffect(() => {
    if (!enabled || !initialLoadComplete) return;

    console.log('🔄 Setting up reactive file sync with', debounceMs, 'ms debounce');

    // Subscribe to entire store state changes
    const unsubscribe = useFileStore.subscribe(
      (state) => {
        console.log('📝 File store changed, scheduling sync...');

        // Clear existing debounce timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        // Set new debounce timer
        debounceTimerRef.current = setTimeout(() => {
          console.log('⏰ Debounce complete, syncing now...');
          syncFiles();
        }, debounceMs);
      }
    );

    return () => {
      unsubscribe();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [enabled, initialLoadComplete, debounceMs, syncFiles]);

  return {
    syncFiles,
    lastSync: lastSyncRef.current,
    isSyncing: syncInProgressRef.current
  };
}
